import { useEffect, useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import { supabase } from '../../lib/supabaseClient';
import { parseBi } from '../../lib/api';
import { saveShow, deleteShow, setVisible } from '../../lib/adminApi';

const empty = {
  id: null, dogName: '', date: '', location: '',
  typePl: '', typeEn: '', achievementPl: '', achievementEn: '', judge: '',
};

const ShowsManagementPage = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('shows').select('*').order('date', { ascending: false });
    setShows((data || []).map((s) => {
      const type = parseBi(s.type);
      const ach = parseBi(s.achievement);
      return {
        id: s.id, dogName: s.dog_name, date: s.date || '', location: s.location || '',
        typePl: type.pl, typeEn: type.en, achievementPl: ach.pl, achievementEn: ach.en,
        judge: s.judge || '', visible: s.visible !== false,
      };
    }));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleVisible = async (s) => {
    try { await setVisible('shows', s.id, s.visible === false); await load(); }
    catch (err) { alert(err.message); }
  };

  const handleSave = async () => {
    if (!editing.dogName.trim()) { alert('Podaj psa'); return; }
    setSaving(true);
    try { await saveShow(editing); setEditing(null); await load(); }
    catch (err) { alert('Błąd zapisu: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Usunąć ten wpis wystawy?')) return;
    try { await deleteShow(s.id); await load(); }
    catch (err) { alert('Błąd usuwania: ' + err.message); }
  };

  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">Łącznie wpisów: <span className="font-semibold">{shows.length}</span></p>
        <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 bg-brand-accent text-white px-4 py-2 rounded-lg hover:opacity-90">
          <FaPlus /> Dodaj wynik
        </button>
      </div>

      {loading ? <p className="text-gray-500">Ładowanie...</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="p-3">Data</th><th className="p-3">Pies</th><th className="p-3">Miejsce</th>
                <th className="p-3">Osiągnięcie (PL)</th><th className="p-3">EN</th><th className="p-3">Sędzia</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {shows.map((s) => {
                const hidden = s.visible === false;
                return (
                <tr key={s.id} className={`hover:bg-gray-50 ${hidden ? 'opacity-50' : ''}`}>
                  <td className="p-3 whitespace-nowrap">{s.date}</td>
                  <td className="p-3">{s.dogName}{hidden && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">ukryty</span>}</td>
                  <td className="p-3">{s.location}</td>
                  <td className="p-3">{s.achievementPl}</td>
                  <td className="p-3">{s.achievementEn
                    ? <span className="text-gray-600">{s.achievementEn}</span>
                    : <span className="text-red-500 text-xs">brak EN</span>}</td>
                  <td className="p-3 text-gray-500">{s.judge}</td>
                  <td className="p-3 whitespace-nowrap text-right">
                    <button onClick={() => toggleVisible(s)} className={`p-1.5 ${hidden ? 'text-gray-400 hover:text-green-600' : 'text-gray-500 hover:text-brand-accent'}`} title={hidden ? 'Pokaż na stronie' : 'Ukryj na stronie'}>{hidden ? <FaEyeSlash /> : <FaEye />}</button>
                    <button onClick={() => setEditing(s)} className="p-1.5 text-gray-500 hover:text-brand-accent"><FaEdit /></button>
                    <button onClick={() => handleDelete(s)} className="p-1.5 text-gray-500 hover:text-red-600"><FaTrash /></button>
                  </td>
                </tr>
                );
              })}
              {shows.length === 0 && <tr><td colSpan={7} className="p-6 text-gray-500">Brak wpisów.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl my-8">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">{editing.id ? 'Edycja wyniku' : 'Nowy wynik'}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pies *</label>
                <input value={editing.dogName} onChange={(e) => set('dogName', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Gina - Harizma Virginiya" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input type="date" value={editing.date || ''} onChange={(e) => set('date', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Miejscowość</label>
                  <input value={editing.location} onChange={(e) => set('location', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typ wystawy</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-400">Polski</span>
                    <input value={editing.typePl} onChange={(e) => set('typePl', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Międzynarodowa Wystawa Psów Rasowych" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">English</span>
                    <input value={editing.typeEn} onChange={(e) => set('typeEn', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="International Dog Show" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Osiągnięcie</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-400">Polski</span>
                    <input value={editing.achievementPl} onChange={(e) => set('achievementPl', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Zwycięzca Rasy" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">English</span>
                    <input value={editing.achievementEn} onChange={(e) => set('achievementEn', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Best of Breed" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sędzia</label>
                <input value={editing.judge} onChange={(e) => set('judge', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Anuluj</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-md bg-brand-accent text-white hover:opacity-90 disabled:opacity-50">
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowsManagementPage;
